import { getStoredToken } from '../auth/cognito';

const API_URL = 'https://0emko5cw6f.execute-api.ca-central-1.amazonaws.com/prod';

function getAuthHeaders() {
  const token = getStoredToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token || '',
  };
}

export interface UserProfile {
  userId: string;
  email: string;
  plan: 'free' | 'paid';
  orgId: string | null;
  role: string | null;
}

export async function getMyProfile(): Promise<UserProfile | null> {
  try {
    const res = await fetch(`${API_URL}/pub/me`, { headers: getAuthHeaders() });
    return await res.json();
  } catch {
    return null;
  }
}

export async function redeemCode(code: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/pub/redeem`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || 'Invalid or expired demo code.' };
    return data;
  } catch {
    return { error: 'Network error. Please try again.' };
  }
}

const CHECKOUT_STARTED_KEY = 'pe_pub_checkout_started';

export function markCheckoutStarted(): void {
  localStorage.setItem(CHECKOUT_STARTED_KEY, '1');
}

export function hasAbandonedCheckout(): boolean {
  return localStorage.getItem(CHECKOUT_STARTED_KEY) === '1';
}

export function clearAbandonedCheckout(): void {
  localStorage.removeItem(CHECKOUT_STARTED_KEY);
}

export async function createCheckoutSession(withDiscount = false): Promise<{ url?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/pub/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ discount: withDiscount ? 10 : 0 }),
    });
    return await res.json();
  } catch {
    return { error: 'Network error. Please try again.' };
  }
}

export async function syncProgress(module: string, xpEarned: number): Promise<void> {
  try {
    await fetch(`${API_URL}/pub/progress`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ module, xpEarned }),
    });
  } catch {
    // fire-and-forget — local XP state is source of truth
  }
}

export async function joinOrg(code: string): Promise<{ success?: boolean; orgName?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/pub/join`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code: code.toUpperCase() }),
    });
    return await res.json();
  } catch {
    return { error: 'Network error. Please try again.' };
  }
}

export async function getMyOrg(): Promise<{ org: OrgSummary | null; role: string | null }> {
  try {
    const res = await fetch(`${API_URL}/pub/org/me`, { headers: getAuthHeaders() });
    return await res.json();
  } catch {
    return { org: null, role: null };
  }
}

export interface OrgSummary {
  orgId: string;
  name: string;
  joinCode: string;
  plan: string;
  seats: number;
  seatsUsed: number;
  status: string;
}

export interface OrgMember {
  userId: string;
  email: string;
  role: string;
  status: string;
  joinedAt: string;
  joinMethod: string;
}

export interface MemberProgress {
  userId: string;
  module: string;
  xpEarned: number;
  completedAt: string;
}

export async function getOrgUsers(): Promise<OrgMember[]> {
  try {
    const res = await fetch(`${API_URL}/pub/org/users`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.users || [];
  } catch {
    return [];
  }
}

export async function getOrgProgress(): Promise<MemberProgress[]> {
  try {
    const res = await fetch(`${API_URL}/pub/org/progress`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.progress || [];
  } catch {
    return [];
  }
}
